<script setup lang="ts">
import {computed, ref} from 'vue'
import {useStore} from 'vuex'
import { toTypedSchema } from '@vee-validate/zod'
import * as z from 'zod'

import { Org } from '@/types'

import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/toast'
import { PlusCircle } from 'lucide-vue-next'

const store = useStore()

const props = defineProps<{
  org: Org
}>()

const isOpen = ref(false)

const close = () => {
  isOpen.value = false
}

const formSchema = toTypedSchema(z.object({
  name: z.string().min(3).max(255).default('Planet API'),
  apikey: z.string().min(3)
}))

const onSubmit = async (values: any) => {
  await store.dispatch('org/addApiKey', {
    orgId: props.org.organization_id,
    ...values
  })
  toast({title: `Apikey add`,})
  close()
}
</script>

<template>
  <Dialog :open="isOpen" @update:open="isOpen = $event">
    <DialogTrigger>
      <slot>
        <Button class="px-2" variant="ghost">
          <PlusCircle class="text-gray-600" />
        </Button>
      </slot>
    </DialogTrigger>
    <DialogContent class="max-w-[350px]">
      <DialogHeader>
        <DialogTitle>Add API Key</DialogTitle>
        <DialogDescription></DialogDescription>
      </DialogHeader>

      <Form class="space-y-3" :validation-schema="formSchema" @submit="onSubmit">
        <FormField v-slot="{ componentField }" name="name">
          <FormItem>
            <FormLabel>Name</FormLabel>
            <FormControl>
              <Input v-bind="componentField" disabled />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>

        <FormField v-slot="{ componentField }" name="apikey">
          <FormItem>
            <FormLabel>API Key</FormLabel>
            <FormControl>
              <Input v-bind="componentField" />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>

        <DialogFooter>
          <Button variant="ghost" @click="close">Close</Button>
          <Button type="submit">Create</Button>
        </DialogFooter>
      </Form>
    </DialogContent>
  </Dialog>
</template>
